/**
 * Breadcrumbs — the CATALOG trails: products, collections and blog articles
 * (FRIDGE-269, Phase 1). The same builder as lib/breadcrumbs.ts — same roots,
 * same types, same rules — kept in its own module ONLY because these three
 * helpers need lib/collections (the 103-family canonical map) and
 * lib/breadcrumb-utils (formatCollectionName / formatProductTitle /
 * formatBlogTitle → lib/brand-rules), and a static import of those from the
 * shared module would ship the whole taxonomy map to every client bundle that
 * renders a trail (89 tool pages, the blog index, /local, /filters, /press,
 * the hubs …) — a First Load JS cost scripts/bundle-budget.mjs exists to
 * refuse. Server templates and the PDP client (which already carries those
 * modules) import from here; everything else imports lib/breadcrumbs.ts.
 *
 * Rules (docs/Page-Types-And-Templates.md §5/§6):
 *  - Collection names are PLURAL, always through formatCollectionName().
 *  - The PDP family crumb is the canonical collection (never singular, never
 *    /collections/all). When no canonical collection maps, the appliance
 *    family (Refrigerators / Freezers) is used when the product's own type or
 *    title names it; otherwise the level is OMITTED rather than invented.
 *  - The article leaf is the formatted title the Article headline uses.
 *
 * Guards: tests/breadcrumbs-helpers.test.ts, tests/breadcrumbs-visible-equals-
 * schema.test.ts, tests/breadcrumbs-source-pin.test.ts.
 */

import { canonicalSlugForTypeOrSlug, inferCanonicalSlugFromText } from '@/lib/collections'
import { formatCollectionName, formatProductTitle, formatBlogTitle } from '@/lib/breadcrumb-utils'
import { HOME_CRUMB, SECTION_ROOTS, type BreadcrumbTrail, type Crumb } from '@/lib/breadcrumbs'
import { isFilterType, isTruePartType } from '@/lib/parts/fpn'

function clean(s: string | null | undefined): string {
  return (s ?? '').trim()
}

// ---------------------------------------------------------------------------
// Collections
// ---------------------------------------------------------------------------

/**
 * /collections/[slug] — `Fridge.com / Collections / {Collection}`.
 * The leaf is the page H1 (`collections.title`, plural by the data layer);
 * when no title is in hand it is the PLURAL canonical display name of the
 * slug via formatCollectionName (registry §5 plural rule).
 */
export function collectionBreadcrumbs(collection: {
  slug: string
  title?: string | null
}): BreadcrumbTrail {
  const name = clean(collection.title) || formatCollectionName(collection.slug)
  return [HOME_CRUMB, SECTION_ROOTS.collections, { name }]
}

// ---------------------------------------------------------------------------
// Products (PDP)
// ---------------------------------------------------------------------------

/**
 * Canonical family slugs that name a WHOLE appliance family. When the type
 * resolves to one of these, a more specific canonical inferred from the title
 * wins ("refrigerators" → "counter-depth-refrigerators"), matching the chain
 * app/products/[slug]/page.tsx has used for the schema crumb since FRIDGE-271.
 */
const GENERIC_FAMILIES = new Set(['refrigerators', 'freezers'])

/**
 * Product types that are PARTS, not appliances — the ONE definition lives in
 * lib/parts/fpn.ts (PART_PRODUCT_TYPES: the filter types, gasket, sensor and
 * the staged 'part'). FRIDGE-333: a part's trail is `Fridge.com / Parts /
 * {part}` (partBreadcrumbs below) — never an appliance family, never a
 * collection — because a part lives at /parts/<slug>, not under a collection.
 * The two legacy vocabulary words ('accessory', 'accessories') are kept out of
 * the family fallback for the same reason.
 */
const NON_APPLIANCE_TYPES = new Set(['parts', 'accessory', 'accessories'])

/**
 * The canonical collection slug for a product, from its type first (with a
 * plural retry so "refrigerator" → "refrigerators"), then the title when the
 * type is generic or unmapped. Null when nothing maps.
 */
export function productCollectionSlug(product: {
  type?: string | null
  title?: string | null
}): string | null {
  const type = clean(product.type)
  const title = clean(product.title)
  const canonFromType =
    canonicalSlugForTypeOrSlug(type) || (type ? canonicalSlugForTypeOrSlug(`${type}s`) : null)
  const canonFromTitle = inferCanonicalSlugFromText(title)
  if (canonFromTitle && (!canonFromType || GENERIC_FAMILIES.has(canonFromType))) return canonFromTitle
  return canonFromType || null
}

/**
 * The appliance family a product belongs to when no canonical collection
 * maps — decided ONLY from words the product's own type/title contain.
 * "refrigerator"/"fridge" wins over "freezer" (a fridge with a freezer
 * compartment is a refrigerator); "freezerless" contains no whole word
 * "freezer" and correctly lands on Refrigerators. Parts types never map.
 */
export function productFamilySlug(product: {
  type?: string | null
  title?: string | null
}): 'refrigerators' | 'freezers' | null {
  const type = clean(product.type).toLowerCase()
  if (isFilterType(type) || isTruePartType(type) || NON_APPLIANCE_TYPES.has(type)) return null
  const text = `${type} ${clean(product.title)}`.toLowerCase().replace(/[_-]+/g, ' ')
  if (/\b(refrigerators?|fridges?)\b/.test(text)) return 'refrigerators'
  if (/\bfreezers?\b/.test(text)) return 'freezers'
  return null
}

/**
 * /part/[slug] — `Fridge.com / Parts / {Part}` (FRIDGE-333 · FRIDGE-389). The
 * leaf is the part's formatted title, exactly as the PDP leaf is; the middle
 * crumb is the parts store root (lib/breadcrumbs.ts SECTION_ROOTS.parts),
 * never an appliance family and never a collection.
 */
export function partBreadcrumbs(part: { title?: string | null; leafName?: string | null }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.parts, { name: leafCrumbName(part) }]
}

/**
 * The leaf crumb prints the page's H1 label when the caller supplies it
 * (FRIDGE-1031, part detail row, 09/17/2026). A part or filter's <h1> is the
 * honest heading built from what it IS (FRIDGE-538: brand + type + part number
 * or "for <OEM filter>"), while `title` is the marketplace string it was
 * imported with — so the trail said "MaksPro WR24X10147 Door Gasket Seal
 * Compatible with GE …" under an H1 that said something else. `title` still
 * drives everything else (collection resolution, the appliance leaf); only the
 * printed leaf follows the heading, and only when a heading was supplied.
 */
function leafCrumbName(product: { title?: string | null; leafName?: string | null }): string {
  const heading = (product.leafName ?? '').trim()
  return heading || formatProductTitle(clean(product.title))
}

/**
 * /filter/[slug] — `Fridge.com / Filters / {Filter}` (FRIDGE-396: a filter is
 * not a part; its middle crumb is the filter STORE root at /filter, never the
 * /filters editorial hub family and never the parts store).
 */
export function filterProductBreadcrumbs(filter: { title?: string | null; leafName?: string | null }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.filterStore, { name: leafCrumbName(filter) }]
}

/**
 * /products/[slug] — `Fridge.com / {Canonical collection} / {Product}`.
 * The middle crumb's name and url derive from the SAME canonical slug so
 * they can never disagree (FRIDGE-271); it is plural through
 * formatCollectionName and never links /collections/all. When neither a
 * canonical collection nor an appliance family maps, the trail is
 * `Fridge.com / {Product}` — omitted, not invented.
 *
 * A PART or a FILTER (lib/parts/fpn.ts) is not a product of any collection:
 * its trail is partBreadcrumbs() / filterProductBreadcrumbs() — on both the
 * server (page.tsx) and the client-only fallback (product-client.tsx), which
 * both call this one function.
 */
export function productBreadcrumbs(product: {
  type?: string | null
  title?: string | null
  /** The page's H1 label; honored on the part and filter trails only (see leafCrumbName). */
  leafName?: string | null
}): BreadcrumbTrail {
  if (isFilterType(product.type)) return filterProductBreadcrumbs(product)
  if (isTruePartType(product.type)) return partBreadcrumbs(product)
  const leaf: Crumb = { name: formatProductTitle(clean(product.title)) }
  const slug = productCollectionSlug(product) ?? productFamilySlug(product)
  if (!slug) return [HOME_CRUMB, leaf]
  return [HOME_CRUMB, { name: formatCollectionName(slug), url: `/collections/${slug}` }, leaf]
}

// ---------------------------------------------------------------------------
// Blog articles
// ---------------------------------------------------------------------------

/**
 * /blogs/news/[slug] — `Fridge.com / Blog / {Article}`. The leaf is the
 * article title through formatBlogTitle, the same text the Article schema
 * headline uses. Built once on the server (app/blogs/news/[slug]/page.tsx)
 * and handed to the client as a prop.
 */
export function blogArticleBreadcrumbs(article: { title: string }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.blog, { name: formatBlogTitle(clean(article.title)) }]
}
